import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/Bits";
import { AsiaMap } from "@/components/site/AsiaMap";
import { WorkflowDiagram } from "@/components/site/WorkflowDiagram";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import { Users } from "lucide-react";

export default function About() {
  const settings = useSettings();
  useSeo({
    title: "About",
    description: "ThinkStack is an enterprise AI transformation partner headquartered in Singapore, working with leaders across Asia.",
    path: "/about",
  });
  const [leaders, setLeaders] = useState(null);
  useEffect(() => {
    api.get("/leaders").then(({ data }) => setLeaders(data)).catch(() => setLeaders([]));
  }, []);

  return (
    <div>
      <section className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-12 pt-16 pb-16 md:pt-20 md:pb-20">
          <Reveal>
            <p className="overline text-restrained-blue mb-6">About</p>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.98] text-deep-navy max-w-4xl">A partner, not a vendor.</h1>
            <p className="mt-8 text-xl md:text-2xl text-charcoal/70 max-w-3xl font-serif leading-snug">
              {settings?.description || "ThinkStack is an enterprise AI transformation partner headquartered in Singapore, working with leaders across Asia to redesign business processes."}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4"><Reveal><SectionHeading overline="Why we exist" title="Redesign first. Technology in service of it." /></Reveal></div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <div className="editorial-body">
                <p>Enterprises do not lack access to technology. They lack the discipline to redesign the work before automating it. ThinkStack exists to bring that discipline — combining consulting, implementation and applied AI into a single method that keeps the business outcome in front of the tool.</p>
                <p>We hold ourselves to a plain editorial standard: credible, concise and outcome-led. We do not claim clients, results, certifications or partnerships we cannot evidence, and we would rather say what we do not yet know than fabricate confidence.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-[#E5E5E5] bg-off-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24">
          <Reveal><SectionHeading overline="How we work" title="One method, applied with judgement." className="max-w-2xl" /></Reveal>
          <div className="mt-12"><Reveal delay={0.1}><WorkflowDiagram /></Reveal></div>
        </div>
      </section>

      <section className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5"><Reveal><SectionHeading overline="Where we are" title="Singapore, working across Asia." /><p className="mt-6 text-charcoal/70 text-lg">Our home in Singapore keeps us close to the operating pressures leaders face across the region.</p></Reveal></div>
          <div className="lg:col-span-6 lg:col-start-7"><Reveal delay={0.1}><AsiaMap /></Reveal></div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24">
          <Reveal><SectionHeading overline="Leadership" title="Our people." className="max-w-2xl" /></Reveal>
          {leaders === null ? (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">{[0,1,2].map(i => <div key={i} className="h-64 bg-off-white animate-pulse border border-[#E5E5E5]" />)}</div>
          ) : leaders.length === 0 ? (
            <div data-testid="leaders-empty" className="mt-12 border border-dashed border-[#E5E5E5] py-20 text-center">
              <Users className="mx-auto text-charcoal/30" size={38} />
              <h3 className="font-serif text-2xl text-deep-navy mt-5">Leadership profiles coming soon</h3>
              <p className="text-charcoal/60 mt-3 max-w-md mx-auto">We publish leadership details only once verified. Profiles will appear here as they are confirmed.</p>
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]" data-testid="leaders-list">
              {leaders.map((l) => (
                <div key={l.id} className="bg-white p-8">
                  {l.photo ? (
                    <div className="aspect-square overflow-hidden mb-5"><img src={l.photo} alt={l.name} className="w-full h-full object-cover grayscale" /></div>
                  ) : (
                    <div className="aspect-square bg-off-white mb-5 flex items-center justify-center"><span className="font-serif text-5xl text-deep-navy/30">{(l.name || "?")[0]}</span></div>
                  )}
                  <h3 className="font-serif text-2xl text-deep-navy">{l.name}</h3>
                  <p className="overline text-restrained-blue mt-1">{l.title}</p>
                  <p className="mt-4 text-charcoal/70 text-sm leading-relaxed">{l.bio}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
